/**
 * WorkshopPickerBody — the picker's scrolling middle: the loading, error and
 * empty states, and then either the dense month calendar or the grouped list.
 *
 * Split out of {@link WorkshopPicker} so that component stays a shell — and so
 * the four non-happy states are readable in one screen instead of buried under
 * the dialog chrome.
 */
import { Loader2 } from 'lucide-react';

import { WorkshopCalendarPicker } from '../../../shared/workshopCalendarPicker';

import type { PickerViewMode } from './pickerViewMode';
import type { PickerWorkshop } from './pickerWorkshop';
import { toCalendarEvent } from './WorkshopPickerHelpers';
import { WorkshopPickerList } from './WorkshopPickerList';

export interface WorkshopPickerBodyProps {
  viewMode: PickerViewMode;
  loading: boolean;
  error: boolean;
  /** Already filtered by whatever owns the data. */
  workshops: PickerWorkshop[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDayOverflowClick: (day: Date) => void;
  month: Date;
  onMonthChange: (month: Date) => void;
  /** What to say when the list is empty — the two callers have different
   *  reasons for an empty month and a shared string would be wrong for one. */
  emptyMessage: string;
  errorMessage: string;
}

export function WorkshopPickerBody({
  viewMode,
  loading,
  error,
  workshops,
  selectedId,
  onSelect,
  onDayOverflowClick,
  month,
  onMonthChange,
  emptyMessage,
  errorMessage,
}: WorkshopPickerBodyProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-stone-300" />
      </div>
    );
  }
  if (error) {
    return (
      <p className="py-10 text-center text-sm text-red-600 dark:text-red-400">{errorMessage}</p>
    );
  }
  if (workshops.length === 0) {
    return <p className="py-10 text-center text-sm text-stone-500">{emptyMessage}</p>;
  }
  if (viewMode === 'calendar') {
    const events = workshops.map(toCalendarEvent);
    return (
      <div className="nb-dashboards flex-1 min-h-0">
        {/* scroll={false}: the dialog body (above) is the single scroll
            container — the grid must not add a second nested scrollbar.
            dense: compact one-line pills capped at 3 + a day drawer instead
            of the default popover (BON-142). */}
        <WorkshopCalendarPicker
          events={events}
          selectedId={selectedId}
          onSelect={onSelect}
          hint="Pick a workshop from the calendar"
          scroll={false}
          dense
          onDayOverflowClick={onDayOverflowClick}
          month={month}
          onMonthChange={onMonthChange}
        />
      </div>
    );
  }
  return <WorkshopPickerList workshops={workshops} selectedId={selectedId} onSelect={onSelect} />;
}
